import { useCallback, useEffect, useMemo, FC, useState } from "react";

import {
  BoostingCampaign,
  Campaign,
  CampaignType,
} from "@vviinn/user-managment-client-ts";
import { Rocket } from "lucide-react";

import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";
import { compareAsStrings } from "../../helpers";

import {
  useAppSelector,
  fetchCategories,
  fetchAccessibleSites,
  fetchReferenceSiteId,
  setIsExtendedCard,
  setShowOnlyActive,
  setSelectedFilters,
} from "../../store";

import { Restricted } from "../../components/Restricted";
import {
  ListWrapper,
  ListSettings,
  FilterEntry,
} from "../../ui/layout/listPages";

import { AppearedContent } from "../../components/DataDisplay/AppearedContent";
import { fetchCampaignsAttributes } from "../../store/features/campaigns/fetch";

import { MEDIUM_ICON_SIZE_STYLES } from "../../ui/styles";
import { styledIcon } from "../../ui/layout/general/styledIcon";
import { campaignTypes, campaignTypeTitles } from "../../campaign/constants";
import { CampaignFilterType } from "../../campaign/list/types";
import { CampaignTile } from "../../campaign/list/components";
import { selectCampaigns } from "../../campaign/list/utils";
import { selectBoosting } from "../../campaign/boosting/utils";
import { BoostingTile } from "../../campaign/boosting/components/BoostingTile";
import { handleCampaign } from "../../store/features/campaigns/handleCampaign";
import { useOutletContext } from "react-router";
import { CompanyAndSiteIds } from "../types";

const LIST_TITLE = "Campaigns";
const SINGLE_ITEM_TITLE = "campaign";

const noMatchingItems = (
  campaigns: Campaign[],
  boostingCount: number,
  selectedFilters: CampaignFilterType[],
) => {
  const matchingCampaigns = selectCampaigns(
    campaigns,
    "",
    false,
    selectedFilters,
  );
  const hasMatchingBoosting =
    (selectedFilters.length === 0 || selectedFilters.includes("BOOSTING")) &&
    boostingCount > 0;
  return !matchingCampaigns.length && !hasMatchingBoosting;
};

// The two domains are filtered independently (selectCampaigns/selectBoosting) but must
// render as one alphabetically-interleaved list, not "all campaigns then all boosting" —
// tiles look the same to the user regardless of which domain they come from. Always
// sorted by name, silently, with no user-facing sort control: boosting campaigns have no
// "date" equivalent to interleave by, so a sort-mode toggle would only correctly order
// one of the two domains — see ListSettings.tsx for how to reintroduce a sort selector if
// a future requirement needs one.
type CombinedListItem =
  | { kind: "campaign"; campaign: Campaign }
  | { kind: "boosting"; boosting: BoostingCampaign };

const combineSorted = (
  campaigns: Campaign[],
  boosting: BoostingCampaign[],
): CombinedListItem[] => {
  const items: CombinedListItem[] = [
    ...campaigns.map((campaign) => ({ kind: "campaign" as const, campaign })),
    ...boosting.map((boostingCampaign) => ({
      kind: "boosting" as const,
      boosting: boostingCampaign,
    })),
  ];
  items.sort((a, b) =>
    compareAsStrings(
      a.kind === "campaign" ? a.campaign.name : a.boosting.name,
      b.kind === "campaign" ? b.campaign.name : b.boosting.name,
    ),
  );
  return items;
};

const selectFilters = (
  campaigns: Campaign[],
  hasBoosting: boolean,
): FilterEntry[] => {
  const possibleFilterTypes: CampaignType[] = [];
  const initFilters: FilterEntry[] = [];
  campaigns.forEach((campaign) => {
    if (!possibleFilterTypes.includes(campaign.type)) {
      possibleFilterTypes.push(campaign.type);
    }
  });
  if (possibleFilterTypes.length > 0) {
    possibleFilterTypes.sort(
      (a, b) => campaignTypes.indexOf(a) - campaignTypes.indexOf(b),
    );
    possibleFilterTypes.forEach((filterType) => {
      initFilters.push({
        text: campaignTypeTitles[filterType],
        entry: filterType,
        icon: styledIcon(
          filterType,
          `${MEDIUM_ICON_SIZE_STYLES} text-stone-400`,
        ),
      });
    });
  }
  if (hasBoosting) {
    initFilters.push({
      text: "Boosting",
      entry: "BOOSTING",
      icon: (
        <Rocket className={`${MEDIUM_ICON_SIZE_STYLES} text-stone-400`} />
      ),
    });
  }
  return initFilters;
};

export const Campaigns: FC = () => {
  const { companyId, siteId } = useOutletContext<CompanyAndSiteIds>();
  const { campaigns } = useAppSelector((state) => state.campaigns);
  const { boosting } = useAppSelector((state) => state.boosting);
  const handlerParams = useRequestHandlers();
  const { handleUpsertCampaignData } = handleCampaign(handlerParams, {
    companyId,
    siteId,
  });
  const { getApi, dispatch } = handlerParams;

  const categories = useAppSelector((state) => state.categories.categories);
  const campaignsLoaded = useAppSelector(
    (state) => state.campaigns.status === "fulfilled",
  );

  const { isExtendedCard, showOnlyActive, selectedFilters } = useAppSelector(
    (state) => state.ui.campaigns,
  );

  const [search, setSearch] = useState("");

  const serviceCampaignCountById = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of campaigns) {
      for (const lbc of c.boosting?.linkedBoostingCampaigns ?? []) {
        const id = lbc.campaign?.id;
        if (id) counts[id] = (counts[id] ?? 0) + 1;
      }
    }
    return counts;
  }, [campaigns]);

  const filteringOptions = selectFilters(campaigns, boosting.length > 0);
  const currentCampaigns = selectCampaigns(
    campaigns,
    search,
    showOnlyActive,
    selectedFilters,
  );
  const currentBoosting = selectBoosting(
    boosting,
    search,
    showOnlyActive,
    selectedFilters,
    serviceCampaignCountById,
  );
  const combinedItems = combineSorted(currentCampaigns, currentBoosting);

  const hasItems = campaigns.length > 0 || boosting.length > 0;
  const totalCount = currentCampaigns.length + currentBoosting.length;

  const loadCampaignsDetails = useCallback(async () => {
    const api = await getApi();
    const fetchingArgs = { api, value: { companyId, siteId } };
    dispatch(fetchCampaignsAttributes(fetchingArgs));
    dispatch(fetchAccessibleSites(api));
    dispatch(fetchReferenceSiteId(fetchingArgs));
  }, [companyId, siteId, getApi, dispatch]);

  const updateCategories = useCallback(async () => {
    const api = await getApi();
    await dispatch(fetchCategories({ api, value: { companyId, siteId } }));
  }, [companyId, siteId, getApi, dispatch]);

  const handleSwitchChange = (id: Campaign["id"]) => async (value: boolean) => {
    const campaign = campaigns.find((c) => c.id === id);
    if (!campaign) return;
    return await handleUpsertCampaignData(
      campaign,
      {
        requestType: "campaign",
        dataToUpdate: { active: value },
      },
      {
        info: ` ${value ? "Activating" : "Deactivating"} campaign`,
        resultMessages: {
          successMessage: `Campaign ${value ? "activated" : "deactivated"}`,
          errorMessage: "Could not change campaign",
        },
      },
    );
  };

  useEffect(() => {
    if (campaigns.length === 0) loadCampaignsDetails();
    if (categories === undefined) updateCategories();
  }, [siteId]);

  useEffect(() => {
    if (
      siteId &&
      hasItems &&
      selectedFilters.length &&
      noMatchingItems(campaigns, boosting.length, selectedFilters)
    ) {
      dispatch(setSelectedFilters({ feature: "campaigns", value: [] }));
    }
  }, [
    siteId,
    campaigns.length,
    boosting.length,
    dispatch,
    selectedFilters,
    campaigns,
    boosting,
  ]);

  return (
    <ListWrapper
      title={LIST_TITLE}
      newItemTitle={SINGLE_ITEM_TITLE}
      createPermissions="campaign:campaign:create"
    >
      <AppearedContent
        isLoaded={campaignsLoaded}
        skeletonOptions={{ amount: 1 }}
      >
        {campaignsLoaded && (
          <>
            {hasItems && (
              <ListSettings
                title={LIST_TITLE.toLowerCase()}
                quantity={totalCount}
                initExtended={isExtendedCard}
                handleExtended={(value) => {
                  dispatch(setIsExtendedCard({ feature: "campaigns", value }));
                }}
                initSearch={search}
                searchMessage="by title"
                handleSearch={setSearch}
                activeFilters={selectedFilters}
                feature="campaigns"
                initFilters={filteringOptions}
                initActiveOnly={showOnlyActive}
                handleActiveOnly={(value) => {
                  dispatch(
                    setShowOnlyActive({
                      feature: "campaigns",
                      value,
                    }),
                  );
                }}
              />
            )}
            <Restricted permissions="campaign:campaign:view">
              {hasItems && (
                <div
                  className="grid grid-cols-1 gap-4 sm:grid-cols-1 lg:grid-cols-2"
                  id="campaignsList"
                >
                  {combinedItems.map((item) =>
                    item.kind === "campaign" ? (
                      <CampaignTile
                        campaign={item.campaign}
                        key={item.campaign.id}
                        handleSwitchChange={handleSwitchChange}
                        isExtended={isExtendedCard}
                        categories={categories}
                      />
                    ) : (
                      <BoostingTile
                        key={item.boosting.id}
                        boosting={item.boosting}
                        isExtended={isExtendedCard}
                        linkedServiceCount={
                          serviceCampaignCountById[item.boosting.id ?? ""] ?? 0
                        }
                      />
                    ),
                  )}
                </div>
              )}

              {!hasItems && campaignsLoaded && (
                <p className="text-primary">This site has no campaigns now.</p>
              )}
            </Restricted>
          </>
        )}
      </AppearedContent>
    </ListWrapper>
  );
};
